import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { isCurseActive, type RaidCurseDoc } from '../utils/raidCurse'
import { useWorkspace } from './useWorkspace'
import { useCredits } from './useCredits'
import { useTimeline } from './useTimeline'
import {
  hydrateGamificationProfile,
  type EquippableSlotField,
  type ProfileShowcasePatch,
} from '../services/gamificationService'
import type { ActivityTrackId, SpinTier, UserGamificationProfile } from '../types/schema'
import { claimTaskCompletionLock } from '../services/taskCompletionLock'
import { recordContribution } from '../services/heatmapService'
import { getNewlyCompletedChecklistIds } from '../services/checklistContributionCapture'
import { owningGoalIdForChecklist, recordLineBuildOutcome } from '../services/lineLaggingUsageObserver'
import { emitInternal } from '../services/automationOrchestrator'
import { emitTrackEvent, emitWorkAction } from '../utils/gamificationEvents'
import { fireAutomationEvent } from '../services/automationBus'
import { emitExperienceEvent } from '../utils/experienceEvents'
import { createDefaultGamificationProfile } from '../utils/gamificationRegistry'
import { isNoSignInPreviewActive } from '../debug'
import { useAuth } from './useAuth'
import {
  claimExperienceSpin,
  claimExperienceTrack,
  executeExperienceSpin,
  mutateExperienceProfile,
  processExperienceEventSync,
  restoreExperienceStreak,
} from '../services/experienceGamificationService'
import { GamificationContext, type GamificationContextValue } from './gamificationContextCore'

const PENDING_THEME_STORAGE_KEY = 'timelining-pending-theme-equip'
const PENDING_THEME_CONFIRM_TIMEOUT_MS = 60_000

interface PendingThemeStoragePayload {
  uid: string
  equippedThemeId: string
}

const readStoredPendingTheme = (currentUid: string | null): string | null => {
  if (!currentUid || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(PENDING_THEME_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingThemeStoragePayload>
    if (parsed && typeof parsed.uid === 'string' && typeof parsed.equippedThemeId === 'string') {
      if (parsed.uid === currentUid) {
        return parsed.equippedThemeId
      }
      window.localStorage.removeItem(PENDING_THEME_STORAGE_KEY)
      return null
    }
    window.localStorage.removeItem(PENDING_THEME_STORAGE_KEY)
  } catch {
    /* local storage access is best-effort */
  }
  return null
}

const writeStoredPendingTheme = (uid: string, equippedThemeId: string) => {
  if (typeof window === 'undefined') return
  try {
    const payload: PendingThemeStoragePayload = { uid, equippedThemeId }
    window.localStorage.setItem(PENDING_THEME_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* local storage access is best-effort */
  }
}

const clearStoredPendingTheme = (uid: string, expectedThemeId?: string) => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(PENDING_THEME_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Partial<PendingThemeStoragePayload>
    if (parsed && parsed.uid === uid) {
      if (expectedThemeId === undefined || parsed.equippedThemeId === expectedThemeId) {
        window.localStorage.removeItem(PENDING_THEME_STORAGE_KEY)
      }
    }
  } catch {
    /* local storage access is best-effort */
  }
}

const debugProfileStorageKey = (uid: string) => `timelining-debug-gamification-profile:${uid}`

const readDebugProfile = (uid: string): Partial<UserGamificationProfile> | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(debugProfileStorageKey(uid))
    return raw ? JSON.parse(raw) as Partial<UserGamificationProfile> : undefined
  } catch {
    return undefined
  }
}

const persistDebugProfile = (profile: UserGamificationProfile) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(debugProfileStorageKey(profile.uid), JSON.stringify(profile))
  } catch {
    /* local debug persistence is best-effort */
  }
}

export const GamificationProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth()
  const { activeWorkspaceId } = useWorkspace()
  const { elements, checklistItems } = useTimeline()
  const uid = user?.uid ?? null

  const [profile, setProfile] = useState<UserGamificationProfile | null>(() => {
    if (uid && isNoSignInPreviewActive()) {
      return hydrateGamificationProfile(uid, readDebugProfile(uid))
    }
    return null
  })
  const [isLoading, setIsLoading] = useState(() => !isNoSignInPreviewActive())
  const [isProfileFromCache, setIsProfileFromCache] = useState(() => !isNoSignInPreviewActive())
  const [pendingThemeId, setPendingThemeId] = useState<string | null>(() => readStoredPendingTheme(uid))
  const pendingThemeRef = useRef<string | null>(pendingThemeId)
  const [prevUid, setPrevUid] = useState(uid)
  const [subscribedCurse, setSubscribedCurse] = useState(false)
  const isWorkspaceCursed = (!activeWorkspaceId || isNoSignInPreviewActive()) ? false : subscribedCurse
  const resendAttemptedRef = useRef(false)
  const confirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearConfirmTimeout = useCallback(() => {
    if (confirmTimeoutRef.current !== null) {
      clearTimeout(confirmTimeoutRef.current)
      confirmTimeoutRef.current = null
    }
  }, [])

  const startConfirmTimeout = useCallback((expectedThemeId: string, targetUid: string) => {
    clearConfirmTimeout()
    confirmTimeoutRef.current = setTimeout(() => {
      confirmTimeoutRef.current = null
      if (pendingThemeRef.current === expectedThemeId) {
        clearStoredPendingTheme(targetUid, expectedThemeId)
        pendingThemeRef.current = null
        setPendingThemeId(null)
      }
    }, PENDING_THEME_CONFIRM_TIMEOUT_MS)
  }, [clearConfirmTimeout])

  useEffect(() => {
    return () => {
      clearConfirmTimeout()
    }
  }, [clearConfirmTimeout])

  if (prevUid !== uid) {
    setPrevUid(uid)
    setPendingThemeId(readStoredPendingTheme(uid))
    if (uid && isNoSignInPreviewActive()) {
      setProfile(hydrateGamificationProfile(uid, readDebugProfile(uid)))
      setIsLoading(false)
      setIsProfileFromCache(false)
    }
  }

  useEffect(() => {
    resendAttemptedRef.current = false
    clearConfirmTimeout()
    pendingThemeRef.current = readStoredPendingTheme(uid)
  }, [uid, clearConfirmTimeout])

  const creditsContext = useCredits()
  const personalWalletCredits = creditsContext.personalCredits
  // Per-task progress watermark for the 0→100 crossing edge detector.
  // Initialized lazily from the FIRST snapshot so existing 100% tasks don't
  // back-fire on every page load.
  const taskProgressRef = useRef<Map<string, number> | null>(null)
  // Heatmap edge-detector for checklist items: tracks the last-seen
  // `isCompleted` state per checklist item id, fires a contribution write
  // on false → true transitions. Reset on workspace switch like taskProgressRef.
  const checklistDoneRef = useRef<Map<string, boolean> | null>(null)

  // Live subscription to the gamification profile. This provider only mounts
  // inside the authenticated subtree, so `uid` is present in practice; the
  // guard is purely defensive. State is only ever set from the async snapshot
  // callbacks (never synchronously in the effect body).
  useEffect(() => {
    if (!uid || isNoSignInPreviewActive()) return undefined

    const unsubscribe = onSnapshot(
      doc(firestoreDb, 'users', uid, 'gamification', 'profile'),
      { includeMetadataChanges: true },
      (snapshot) => {
        setIsProfileFromCache(snapshot.metadata.fromCache)
        // Hydrate from defaults when the doc doesn't exist yet so Your Page
        // renders immediately for users whose profile hasn't been bootstrapped
        // server-side — otherwise ProfileCanvas hangs on `Loading…` forever.
        const hydrated = hydrateGamificationProfile(
          uid,
          snapshot.exists() ? (snapshot.data() as Partial<UserGamificationProfile>) : undefined,
        )
        setProfile(hydrated)
        setIsLoading(false)

        if (!snapshot.metadata.fromCache) {
          if (pendingThemeRef.current !== null && pendingThemeRef.current === hydrated.equippedThemeId) {
            clearConfirmTimeout()
            clearStoredPendingTheme(uid, pendingThemeRef.current)
            pendingThemeRef.current = null
            setPendingThemeId(null)
          }
        }
      },
      (error) => {
        console.warn('[Gamification] profile snapshot failed', error)
        // Same rationale as the missing-doc branch above: degrade to defaults
        // so the page renders instead of stuck on the loading state.
        setProfile(createDefaultGamificationProfile(uid, new Date().toISOString()))
        setIsLoading(false)
        setIsProfileFromCache(false)
      },
    )

    return unsubscribe
  }, [uid, clearConfirmTimeout])

  // On load: re-send mutateExperienceProfile if a pending theme selection exists for this uid.
  useEffect(() => {
    if (!uid || isNoSignInPreviewActive() || resendAttemptedRef.current) return
    const storedTheme = readStoredPendingTheme(uid)
    if (storedTheme === null) return
    resendAttemptedRef.current = true

    mutateExperienceProfile({ equippedThemeId: storedTheme })
      .then(() => {
        startConfirmTimeout(storedTheme, uid)
      })
      .catch((error) => {
        console.warn('[Gamification] pending theme resend failed', error)
        if (pendingThemeRef.current === storedTheme) {
          clearConfirmTimeout()
          clearStoredPendingTheme(uid, storedTheme)
          pendingThemeRef.current = null
          setPendingThemeId(null)
        }
      })
  }, [uid, startConfirmTimeout, clearConfirmTimeout])


  // Subscribe to raid_curses for the active workspace so we can short-circuit
  // ticket grants client-side AND surface a banner. The server-side guard
  // inside recordTrackEvent is the actual security boundary. State is only
  // ever set from the async snapshot callbacks (never synchronously here).
  useEffect(() => {
    if (!activeWorkspaceId || isNoSignInPreviewActive()) return undefined
    return onSnapshot(
      doc(firestoreDb, 'raid_curses', activeWorkspaceId),
      (snapshot) => {
        const data = snapshot.exists() ? (snapshot.data() as RaidCurseDoc) : undefined
        setSubscribedCurse(isCurseActive(data, new Date().toISOString()))
      },
      () => setSubscribedCurse(false),
    )
  }, [activeWorkspaceId])

  useEffect(() => {
    taskProgressRef.current = null
    checklistDoneRef.current = null
  }, [activeWorkspaceId])

  // Edge-detect TASK element completion (0 → 100%) so we can emit
  // TRACK_FINISH_TASK + TASK_COMPLETE without instrumenting TimelineContext.
  // CAVEAT: this fires once per CLIENT that observes the crossing. A workspace
  // with N online teammates will count one task completion N times for raid
  // damage / XP. Authoritative dedup belongs in a Cloud Function (Phase 4); for
  // now we accept the trade-off and reset the watermark on workspace switch.
  useEffect(() => {
    if (!activeWorkspaceId) {
      taskProgressRef.current = null
      return
    }
    if (isNoSignInPreviewActive()) {
      taskProgressRef.current = null
      return
    }
    const next = new Map<string, number>()
    const prev = taskProgressRef.current
    elements.forEach((element) => {
      if (!['GOAL', 'TASK', 'SUBTASK'].includes(element.type)) return
      const progress = typeof element.progress === 'number' ? element.progress : 0
      next.set(element.id, progress)
      if (prev === null) return

      const previousProgress = prev.get(element.id) ?? 0
      if (previousProgress >= 100 || progress < 100 || !uid) return
      if (element.type === 'GOAL') {
        emitExperienceEvent('TRACK_FINISH_GOAL', { workspaceId: activeWorkspaceId, sourceId: element.id })
        fireAutomationEvent(
          {
            type: 'NODE_COMPLETED',
            workspaceId: activeWorkspaceId,
            eventId: `node-complete-${element.id}-${Date.now()}`,
            actorId: uid,
            payload: { nodeId: element.id, nodeKind: 'GOAL', title: element.title },
          },
          { id: uid },
        )
        return
      }
      if (element.type === 'SUBTASK') {
        emitExperienceEvent('TRACK_FINISH_SUB', { workspaceId: activeWorkspaceId, sourceId: element.id })
        fireAutomationEvent(
          {
            type: 'NODE_COMPLETED',
            workspaceId: activeWorkspaceId,
            eventId: `node-complete-${element.id}-${Date.now()}`,
            actorId: uid,
            payload: { nodeId: element.id, nodeKind: 'SUBTASK', title: element.title },
          },
          { id: uid },
        )
        return
      }
      // Phase 4 cross-client dedup: every online teammate observes
      // this snapshot crossing, but only the transaction-winner emits.
      const taskId = element.id
      const legacyMetrics = element as { soldUnits?: number | null }
      const completedChecklists = Array.isArray(element.checklist)
        ? element.checklist.filter((item) => item?.isCompleted).length
        : 0
      const soldUnitsTotal =
        (typeof legacyMetrics.soldUnits === 'number' ? legacyMetrics.soldUnits : 0) +
        (Array.isArray(element.checklist)
          ? element.checklist.reduce((sum, item) => sum + (typeof item?.soldUnits === 'number' ? item.soldUnits : 0), 0)
          : 0)
      const priority = typeof element.priority === 'number' ? Math.max(1, Math.min(5, element.priority)) : 3
      const weightedProgress = Math.max(1, Math.round(1 + (priority / 5) * 2 + completedChecklists * 0.5))
      const quotaContext = {
        assigneeId: uid,
        delta: {
          tasks_completed: 1,
          checklists_done: completedChecklists,
          sales_units: soldUnitsTotal,
          weighted_progress: weightedProgress,
        },
      }
      void claimTaskCompletionLock(activeWorkspaceId, taskId, uid, { quotaContext }).then((won) => {
        if (!won) return
        emitTrackEvent('TRACK_FINISH_TASK')
        emitWorkAction({ kind: 'TASK_COMPLETE', workspaceId: activeWorkspaceId, subNodeId: taskId })
        fireAutomationEvent(
          {
            type: 'NODE_COMPLETED',
            workspaceId: activeWorkspaceId,
            eventId: `node-complete-${taskId}-${Date.now()}`,
            actorId: uid,
            payload: { nodeId: taskId, nodeKind: 'TASK', title: element.title },
          },
          { id: uid },
        )
        // Heatmap contribution: bump today's tasksCompletedCount cell
        // for the lock-winning observer. Caveat (same as TRACK_FINISH_TASK
        // above): the winner isn't necessarily the user who triggered
        // the completion; correct attribution needs a `completedBy`
        // field + Cloud Function (Phase 4).
        void recordContribution(uid, 'TASK')
        // Fan out to internal orchestrator listeners (QuotaService
        // handles the post-commit coaching threshold + 100% audit).
        void emitInternal(
          {
            type: 'TASK_COMPLETED',
            workspaceId: activeWorkspaceId,
            eventId: `task-complete-${taskId}-${Date.now()}`,
            actorId: uid,
            payload: {
              taskId,
              assigneeId: uid,
              soldUnits: soldUnitsTotal,
              checklistItemsCompleted: completedChecklists,
            },
          },
          { id: uid },
        )
      })
    })
    taskProgressRef.current = next
  }, [activeWorkspaceId, elements, uid])

  // Edge-detect CHECKLIST_ITEM completion (false → true) for the heatmap
  // contribution counter. No cross-client lock here (unlike task completion):
  // each observing client fires its own write; in a single-tab personal
  // workspace this is exactly-once. Multi-tab / team caveat applies same as
  // the task watcher above. Heatmap is observational so over-counting is
  // visual noise, not a correctness break.
  useEffect(() => {
    if (!activeWorkspaceId || !uid) {
      checklistDoneRef.current = null
      return
    }
    if (isNoSignInPreviewActive()) {
      checklistDoneRef.current = null
      return
    }
    const next = new Map<string, boolean>()
    const prev = checklistDoneRef.current
    checklistItems.forEach((item) => {
      const isCompleted = Boolean(item.isCompleted)
      next.set(item.id, isCompleted)
    })
    if (prev !== null) {
      const completedIds = new Set(getNewlyCompletedChecklistIds(prev, checklistItems))
      checklistItems.filter((item) => completedIds.has(item.id)).forEach((item) => {
        emitTrackEvent('TRACK_FINISH_CHECK')
        void recordContribution(uid, 'CHECKLIST')
        const goalId = owningGoalIdForChecklist(item, elements)
        if (goalId) recordLineBuildOutcome(goalId, 'positive')
      })
    }
    checklistDoneRef.current = next
  }, [activeWorkspaceId, checklistItems, elements, uid])

  const requireUid = useCallback(() => {
    if (!uid) throw new Error('You must be signed in to use gamification features.')
    return uid
  }, [uid])

  const spin = useCallback(
    async (tier: SpinTier) => {
      requireUid()
      return executeExperienceSpin(tier)
    },
    [requireUid],
  )

  const claimSpin = useCallback(
    async (claimId: string, doubled: boolean) => {
      requireUid()
      await claimExperienceSpin(claimId, doubled)
    },
    [requireUid],
  )

  const recordTrack = useCallback(
    (trackId: ActivityTrackId, increment = 1) => {
      if (!uid) return
      emitExperienceEvent(trackId, { increment, workspaceId: activeWorkspaceId ?? undefined })
    },
    [activeWorkspaceId, uid],
  )

  const claimTrack = useCallback(
    async (trackId: ActivityTrackId) => {
      if (!uid) return null
      const { collected } = await claimExperienceTrack(trackId)
      return { trackId, collected }
    },
    [uid],
  )

  const rescueStreak = useCallback(
    async (viaAds: boolean) => {
      requireUid()
      await restoreExperienceStreak(viaAds)
    },
    [requireUid],
  )

  const equip = useCallback(
    async (patch: Partial<Record<EquippableSlotField, string>>) => {
      const targetUid = requireUid()
      if (isNoSignInPreviewActive()) {
        setProfile((current) => {
          const next = { ...(current ?? createDefaultGamificationProfile(targetUid, new Date().toISOString())), ...patch }
          persistDebugProfile(next)
          return next
        })
        return
      }
      const hasTheme = 'equippedThemeId' in patch && typeof patch.equippedThemeId === 'string'
      const themeToEquip = patch.equippedThemeId
      if (hasTheme && themeToEquip !== undefined) {
        clearConfirmTimeout()
        pendingThemeRef.current = themeToEquip
        setPendingThemeId(themeToEquip)
        writeStoredPendingTheme(targetUid, themeToEquip)
      }
      try {
        await mutateExperienceProfile(patch)
        if (hasTheme && themeToEquip !== undefined) {
          if (pendingThemeRef.current === themeToEquip) {
            startConfirmTimeout(themeToEquip, targetUid)
          }
        }
      } catch (error) {
        if (hasTheme && themeToEquip !== undefined) {
          if (pendingThemeRef.current === themeToEquip) {
            clearConfirmTimeout()
            clearStoredPendingTheme(targetUid, themeToEquip)
            pendingThemeRef.current = null
            setPendingThemeId(null)
          }
        }
        throw error
      }
    },
    [requireUid, clearConfirmTimeout, startConfirmTimeout],
  )

  const unlock = useCallback(
    async (field: Parameters<GamificationContextValue['unlock']>[0], cosmeticId: string) => {
      const targetUid = requireUid()
      if (isNoSignInPreviewActive()) {
        setProfile((current) => {
          const base = current ?? createDefaultGamificationProfile(targetUid, new Date().toISOString())
          const nextValues = new Set(base[field] ?? [])
          nextValues.add(cosmeticId)
          const next = { ...base, [field]: Array.from(nextValues) }
          persistDebugProfile(next)
          return next
        })
        return
      }
      // Synchronous server confirmation — caller awaits the actual write before
      // showing "unlocked" UI, so a rejection (App Check fail, registry mismatch,
      // rules block) propagates as a thrown error instead of a silent desync.
      await processExperienceEventSync('COSMETIC_UNLOCKED', {
        slot: field,
        cosmeticId,
        workspaceId: activeWorkspaceId ?? undefined,
      })
    },
    [activeWorkspaceId, requireUid],
  )

  const saveShowcase = useCallback(
    async (patch: ProfileShowcasePatch) => {
      const targetUid = requireUid()
      if (isNoSignInPreviewActive()) {
        setProfile((current) => {
          const next = { ...(current ?? createDefaultGamificationProfile(targetUid, new Date().toISOString())), ...patch }
          persistDebugProfile(next)
          return next
        })
        return
      }
      await mutateExperienceProfile(patch)
    },
    [requireUid],
  )

  const value = useMemo<GamificationContextValue>(
    () => ({
      profile,
      isLoading,
      ledgerCredits: personalWalletCredits,
      isWorkspaceCursed,
      isProfileFromCache,
      pendingThemeId,
      spin,
      claimSpin,
      recordTrack,
      claimTrack,
      rescueStreak,
      equip,
      unlock,
      saveShowcase,
    }),
    [
      claimSpin,
      claimTrack,
      equip,
      isLoading,
      isProfileFromCache,
      isWorkspaceCursed,
      pendingThemeId,
      personalWalletCredits,
      profile,
      recordTrack,
      rescueStreak,
      saveShowcase,
      spin,
      unlock,
    ],
  )

  return <GamificationContext.Provider value={value}>{children}</GamificationContext.Provider>
}
